import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, Filter, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';

const statusColor = {
  pending:   'bg-yellow-100 text-yellow-700 border-yellow-200',
  approved:  'bg-green-100  text-green-700  border-green-200',
  rejected:  'bg-red-100    text-red-700    border-red-200',
  cancelled: 'bg-gray-100   text-gray-500   border-gray-200',
};

export default function LeaveManagement() {
  const [requests, setRequests]   = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [filter, setFilter]       = useState('pending');
  const [loading, setLoading]     = useState(true);
  const [remarks, setRemarks]     = useState({});
  const [acting, setActing]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, lt] = await Promise.all([
        api.getLeaveRequests(filter !== 'all' ? { status: filter } : {}),
        api.getLeaveTypes(),
      ]);
      setRequests(lr);
      setLeaveTypes(lt);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, approve) => {
    setActing(id);
    try {
      if (approve) await api.approveLeave(id, remarks[id] || '');
      else         await api.rejectLeave (id, remarks[id] || '');
      await load();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setActing(null); }
  };

  const counts = { all: requests.length };
  ['pending','approved','rejected','cancelled'].forEach(s => {
    counts[s] = requests.filter(r => r.status === s).length;
  });
  const visible = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Leave Management</h1>
          <p className="text-gray-500 text-sm mt-0.5">Review and approve staff leave requests</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Summary chips */}
      <div className="flex gap-3 mb-5 flex-wrap">
        {['all','pending','approved','rejected'].map(s => (
          <button key={s}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors capitalize ${filter === s ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            <span className={`ml-1.5 text-xs font-bold ${filter === s ? 'text-purple-200' : 'text-gray-400'}`}>
              {counts[s] || 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 text-gray-400">No {filter === 'all' ? '' : filter} leave requests</div>
      ) : (
        <div className="space-y-4">
          {visible.map(r => (
            <div key={r.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800 text-base">
                      {r.firstName} {r.lastName}
                    </span>
                    <span className="px-2 py-0.5 rounded text-xs font-bold border bg-purple-50 text-purple-700 border-purple-200">
                      {r.leaveTypeCode}
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${statusColor[r.status]}`}>
                      {r.status}
                    </span>
                    {r.isPaid === false && (
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-orange-50 text-orange-600 border border-orange-200">Unpaid</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {r.leaveTypeName} &nbsp;·&nbsp;
                    {fmt(r.startDate)} → {fmt(r.endDate)} &nbsp;·&nbsp;
                    <strong>{Number(r.days).toFixed(1)} day{r.days != 1 ? 's' : ''}</strong>
                  </p>
                  {r.reason && (
                    <p className="text-sm text-gray-600 mt-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                      {r.reason}
                    </p>
                  )}
                  {r.adminRemarks && (
                    <p className="text-sm mt-1 italic text-gray-400">Admin note: {r.adminRemarks}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">Applied: {fmtTs(r.appliedAt)}</p>
                </div>

                {r.status === 'pending' && (
                  <div className="flex flex-col gap-2 min-w-48">
                    <input
                      type="text"
                      placeholder="Remarks (optional)"
                      value={remarks[r.id] || ''}
                      onChange={e => setRemarks(p => ({ ...p, [r.id]: e.target.value }))}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    />
                    <div className="flex gap-2">
                      <button
                        disabled={acting === r.id}
                        onClick={() => act(r.id, true)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                        <CheckCircle size={14} /> Approve
                      </button>
                      <button
                        disabled={acting === r.id}
                        onClick={() => act(r.id, false)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                        <XCircle size={14} /> Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmt(d) {
  if (!d) return '—';
  try { const dt = new Date(d); return `${dt.getDate()}/${dt.getMonth()+1}/${dt.getFullYear()}`; }
  catch { return d; }
}
function fmtTs(d) {
  if (!d) return '';
  try {
    const dt = new Date(d);
    return `${dt.getDate()}/${dt.getMonth()+1}/${dt.getFullYear()} ${dt.getHours()}:${String(dt.getMinutes()).padStart(2,'0')}`;
  } catch { return d; }
}
